import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import {

  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  Checkbox,
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import { User } from 'firebase/auth';
import { UserProfile } from '@watchlist/shared';
import { SearchService } from '../../services/searchService';
import { getCachedUserProfile } from '../../utils/profileCache';
import { useUserFollow } from '../../contexts/UserFollowContext';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import UserListingCard from '../widgets/UserListingCard';
import EmptyState from '../widgets/EmptyState';
import ContentShimmer from '../widgets/ContentShimmer';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { glassStyles } from '../../tokens/designTokens';
import { font } from '../../utils/fontHelper';
import { useKeyboardAware } from '../../hooks/useKeyboardAware';

interface CollaboratorPickerProps {
  open: boolean;
  onClose: () => void;
  listName: string;
  selectedCollaborators: string[];
  onCollaboratorsChange: (collaboratorIds: string[]) => void;
  user?: User;
}

const CollaboratorPicker: React.FC<CollaboratorPickerProps> = ({
  open,
  onClose,
  listName,
  selectedCollaborators,
  onCollaboratorsChange,
  user,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isKeyboardOpen, bottomPadding, scrollIntoViewOnFocus } = useKeyboardAware();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedCollaborators);

  const { followStats } = useUserFollow();
  const { getUserDisplayName, getUserUsername, getUserAvatar } = useUserProfileCache();

  // Load following list profiles on mount
  const [followingProfiles, setFollowingProfiles] = useState<UserProfile[]>([]);
  const [loadingFollowing, setLoadingFollowing] = useState(false);

  useEffect(() => {
    const loadFollowingProfiles = async () => {
      if (!followStats.followedUsers.length) {
        setFollowingProfiles([]);
        return;
      }

      setLoadingFollowing(true);
      try {
        const profiles = await Promise.all(
          followStats.followedUsers.map(async (userId) => {
            const profile = await getCachedUserProfile(userId);
            return profile;
          })
        );
        setFollowingProfiles(profiles.filter(Boolean) as UserProfile[]);
      } catch (error) {
        console.error('Error loading following profiles:', error);
      } finally {
        setLoadingFollowing(false);
      }
    };

    if (open) {
      loadFollowingProfiles();
      setLocalSelectedIds(selectedCollaborators);
    }
  }, [open, followStats.followedUsers, selectedCollaborators]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await SearchService.searchUsers(searchQuery);
        // Filter out current user
        setSearchResults(results.filter(profile => profile.uid !== user?.uid));
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, user?.uid]);

  const handleToggleCollaborator = (userId: string) => {
    setLocalSelectedIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleDone = () => {
    onCollaboratorsChange(localSelectedIds);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const renderUserItem = (profile: UserProfile) => {
    const isSelected = localSelectedIds.includes(profile.uid);

    return (
      <Box
        key={profile.uid}
        component="button"
        type="button"
        onClick={() => handleToggleCollaborator(profile.uid)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
          cursor: 'pointer',
          width: '100%',
          textAlign: 'left',
          border: 'none',
          background: 'none',
          padding: 0,
          borderRadius: 2,
          transition: 'background-color 0.2s ease, transform 0.2s ease',
          px: 0.75,
          py: 0.75,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.04)'
              : 'rgba(0, 0, 0, 0.04)',
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Checkbox
          checked={isSelected}
          tabIndex={-1}
          disableRipple
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            event.stopPropagation();
            handleToggleCollaborator(profile.uid);
          }}
          sx={{
            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            '&.Mui-checked': {
              color: '#7D7AFF',
            },
          }}
        />
        <Box
          sx={{
            flex: 1,
            pointerEvents: 'none',
          }}
        >
          <UserListingCard
            id={profile.uid}
            name={getUserDisplayName(profile.uid)}
            username={getUserUsername(profile.uid)}
            avatar={getUserAvatar(profile.uid)}
            stats={{
              followers: 0,
              watchlists: 0,
            }}
            showFollowButton={false}
            size="compact"
          />
        </Box>
      </Box>
    );
  };

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? '100%' : 'auto',
        maxHeight: isMobile ? '100%' : '80vh',
        backgroundColor: 'transparent',
      }}
    >
      {/* Header */}
      <BottomSheetDraggableHeader
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          px: 3,
          py: 2,
          flexShrink: 0,
          backdropFilter: 'blur(20px)',
          bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, 0.5)'
            : 'rgba(255, 255, 255, 0.5)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={handleClose} size="small">
            <ArrowLeftIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontFamily: font(),
              fontSize: 18,
              flex: 1,
            }}
          >
            Invite collaborators to "{listName}"
          </Typography>
        </Box>

        {!isMobile && (
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: theme.palette.text.primary,
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>
        )}

        {/* Search Input */}
        <TextField
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={scrollIntoViewOnFocus}
          placeholder="Search any user"
          variant="outlined"
          inputProps={{ style: { fontSize: 16 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              '& fieldset': {
                borderColor: 'transparent',
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px'
              },
            },
          }}
        />
      </BottomSheetDraggableHeader>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          px: 3,
          py: 2,
          pb: isKeyboardOpen ? `${bottomPadding + 80}px` : 2, // Extra space for footer
          transition: 'padding-bottom 0.25s ease-out',
        }}
      >
        {/* Search Results */}
        {searchQuery.trim() ? (
          <>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: font(),
                fontWeight: 600,
                fontSize: 14,
                color: theme.palette.text.secondary,
                mb: 2,
              }}
            >
              Search Results
            </Typography>

            {isSearching ? (
              <ContentShimmer lines={3} />
            ) : searchResults.length > 0 ? (
              searchResults.map(renderUserItem)
            ) : (
              <EmptyState message="No users found. Try a different search term" />
            )}
          </>
        ) : (
          /* Your Connections - Default View */
          <>
            {followingProfiles.length > 0 ? (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontFamily: font(),
                    fontWeight: 600,
                    fontSize: 14,
                    color: theme.palette.text.secondary,
                    mb: 2,
                  }}
                >
                  People You Follow
                </Typography>

                {loadingFollowing ? (
                  <ContentShimmer lines={3} />
                ) : (
                  followingProfiles.map(renderUserItem)
                )}
              </>
            ) : (
              <EmptyState message="No connections yet. Search above to find users to collaborate with" />
            )}
          </>
        )}
      </Box>

      {/* Footer with Done Button */}
      <Box
        sx={{
          p: 2,
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 90,
          backdropFilter: 'blur(20px)',
          bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(0, 0, 0, 0.5)'
            : 'rgba(255, 255, 255, 0.5)',
          borderTop: `1px solid ${theme.palette.divider}`,
          pb: isMobile
            ? `calc(${theme.spacing(2)} + var(--safe-area-inset-bottom))`
            : 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: font(),
              fontSize: 14,
              color: theme.palette.text.secondary,
            }}
          >
            Selected: {localSelectedIds.length} {localSelectedIds.length === 1 ? 'collaborator' : 'collaborators'}
          </Typography>
        </Box>
        <CustomButton
          onClick={handleDone}
          semantic="brand"
          sx={{
            width: '100%',
            py: 1.5,
            fontSize: 16,
            fontWeight: 600,
            textTransform: 'none',
            fontFamily: font(),
          }}
        >
          Done
        </CustomButton>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        height="85dvh"
        zIndex={1300}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 'var(--safe-area-inset-bottom)', minHeight: 0 }}>
          {content}
        </Box>
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        },
      }}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          ...glassStyles.popup(theme.palette.mode === 'dark'),
        },
      }}
    >
      {content}
    </Dialog>
  );
};

export default CollaboratorPicker;
